import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import treasureChestAnimation from '../effects/treasure-chest.json';
import { ProfileEffectOverlay } from '../effects';
import TaskButtonComponent from '../shop/TaskButtonComponent';

const PIXIE_DUST_EFFECT = {
  name: "Pixie Dust",
  slug: "pixie-dust",
  type: "profile-effect",
  width: 450,
  height: 880,
  effects: [
    {
      src: "/uploads/profile-effects/pixie-dust/layer-0.png",
      loop: true,
      start: 0,
      duration: 2880,
      zIndex: 100,
      position: { x: 0, y: 0 },
      loopDelay: 2880,
      width: 450,
      height: 880
    }
  ],
  reducedMotionSrc: "/uploads/profile-effects/pixie-dust/static.png"
};

const RARITY_COLORS = {
  tavallinen: '#78909c',
  harvinainen: '#2196f3',
  eeppinen: '#9c27b0',
  legendaarinen: '#ff9800',
};

const RARITY_LABELS = {
  tavallinen: 'Tavallinen',
  harvinainen: 'Harvinainen',
  eeppinen: 'Eeppinen',
  legendaarinen: 'Legendaarinen',
};

const TYPE_LABELS = {
  avatar: 'Avatar',
  frame: 'Kehys',
  theme: 'Teema',
  task_effect: 'Efekti',
  task_animation: 'Painike',
  background: 'Tausta',
  booster: 'Boosteri',
  tavara: 'Tavara',
};

const TYPE_ICONS = {
  avatar: '👤',
  frame: '🖼️',
  theme: '🎨',
  task_effect: '🎆',
  task_animation: '✨',
  background: '🌄',
  booster: '🚀',
  tavara: '📦',
};

export default function LottieGiftReveal({
  item,
  coinsAwarded = 0,
  oldBalance = 0,
  onComplete,
  isDark = false,
  effect = PIXIE_DUST_EFFECT,
}) {
  const [phase, setPhase] = useState('card'); // card | shaking | opening | reveal | idle
  const [displayCoins, setDisplayCoins] = useState(0);
  const [showItemCard, setShowItemCard] = useState(false);
  const lottieRef = useRef(null);
  const countingStarted = useRef(false);
  const timersRef = useRef([]);

  const accentColor = '#9c27b0';
  const textColor = '#9c27b0';

  const rarityColor = RARITY_COLORS[item?.rarity] || RARITY_COLORS.tavallinen;
  const rarityLabel = RARITY_LABELS[item?.rarity] || RARITY_LABELS.tavallinen;

  const animateCount = useCallback((from, to, duration) => {
    const start = performance.now();
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuart(progress);
      setDisplayCoins(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        const t = setTimeout(() => {
          setShowItemCard(true);
          setPhase('idle');
        }, 300);
        timersRef.current.push(t);
      }
    };
    requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!lottieRef.current) return;

      if (phase === 'shaking') {
        lottieRef.current.playSegments([0, 56], true);
      } else if (phase === 'opening') {
        lottieRef.current.playSegments([56, 103], true);
      } else if (phase === 'idle') {
        lottieRef.current.playSegments([103, 256], true);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [phase]);

  const handleCardTap = () => {
    if (phase !== 'card') return;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase('shaking');

    const t1 = setTimeout(() => {
      setPhase('opening');

      const t2 = setTimeout(() => {
        setPhase('reveal');
        if (coinsAwarded > 0 && !countingStarted.current) {
          countingStarted.current = true;
          animateCount(0, coinsAwarded, 4000);
        } else {
          const t3 = setTimeout(() => {
            setShowItemCard(true);
            setPhase('idle');
          }, 500);
          timersRef.current.push(t3);
        }
      }, 600);
      timersRef.current.push(t2);
    }, 2000);
    timersRef.current.push(t1);
  };

  useEffect(() => {
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  const handleDismiss = () => {
    if (phase === 'idle') {
      onComplete?.();
    }
  };

  const showDarkBg = phase !== 'card';
  const showEffect = phase === 'opening' || phase === 'reveal' || phase === 'idle';
  const showAnimatedChest = phase !== 'card';
  const showCounter = coinsAwarded > 0 && (phase === 'reveal' || phase === 'idle');

  return (
    <Box
      onClick={phase === 'card' ? handleCardTap : (phase === 'idle' ? handleDismiss : undefined)}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        cursor: (phase === 'card' || phase === 'idle') ? 'pointer' : 'default',
        borderRadius: 'inherit',
        overflow: 'hidden',
      }}
    >
      {/* Dark background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showDarkBg ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.9)',
          zIndex: 0,
        }}
      />

      {/* Pixie dust effect */}
      <AnimatePresence>
        {showEffect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ position: 'absolute', inset: 0, zIndex: 1 }}
          >
            <ProfileEffectOverlay
              effect={effect}
              fillContainer
              zIndex={1}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* === CARD PHASE === */}
      <AnimatePresence>
        {phase === 'card' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Box sx={{ position: 'relative', width: 300, height: 340 }}>
              {/* Card background - pulses */}
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  transformOrigin: 'center bottom',
                  zIndex: 1,
                }}
              >
                <Box sx={{
                  bgcolor: isDark ? 'background.default' : '#fff',
                  borderRadius: '24px',
                  height: 260,
                  boxShadow: `0 12px 28px ${accentColor}66`,
                  border: `4px solid ${accentColor}`,
                }} />
              </motion.div>

              {/* Chest - fixed position */}
              <Box sx={{
                position: 'absolute',
                top: -80,
                left: -150,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 2,
              }}>
                <Box sx={{ width: 300 }}>
                  <Lottie
                    animationData={treasureChestAnimation}
                    loop={false}
                    autoplay={false}
                    initialSegment={[0, 1]}
                    style={{ width: '450px', height: 'auto' }}
                  />
                </Box>
              </Box>

              {/* Text */}
              <Box sx={{
                position: 'absolute',
                top: 250,
                bottom: 16,
                left: 0,
                right: 0,
                textAlign: 'center',
                zIndex: 3,
              }}>
                <Typography variant="h6" fontWeight={900} sx={{ color: textColor, mb: 0.5 }}>
                  Lahja!
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', display: 'block' }}
                >
                  Napauta avataksesi 🎁
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* === ANIMATED CHEST PHASES === */}
      <AnimatePresence>
        {showAnimatedChest && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Box sx={{ position: 'relative', width: 300, height: 340 }}>

              {/* Coin counter - above chest (only for shared goal rewards) */}
              <AnimatePresence>
                {showCounter && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 40 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 150, damping: 12 }}
                    style={{
                      position: 'absolute',
                      top: -10,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      zIndex: 15,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '3.5rem',
                        fontWeight: 700,
                        color: '#ffd700',
                        textShadow: '0 4px 24px rgba(255,215,0,0.7), 0 2px 8px rgba(0,0,0,0.5)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      +{displayCoins} 🪙
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Animated Lottie chest */}
              <Box sx={{
                position: 'absolute',
                top: -80,
                left: -150,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 10,
              }}>
                <Box sx={{ width: 300 }}>
                  <Lottie
                    lottieRef={lottieRef}
                    animationData={treasureChestAnimation}
                    loop={phase === 'shaking' || phase === 'idle'}
                    autoplay={false}
                    style={{ width: '450px', height: 'auto' }}
                  />
                </Box>
              </Box>

              {/* Item reveal card - below chest */}
              <AnimatePresence>
                {showItemCard && (
                  <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    style={{
                      position: 'absolute',
                      bottom: -20,
                      left: -20,
                      right: -20,
                      display: 'flex',
                      justifyContent: 'center',
                      zIndex: 15,
                    }}
                  >
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.98)',
                      borderRadius: '20px',
                      p: 2,
                      px: 3,
                      textAlign: 'center',
                      boxShadow: `0 8px 32px ${rarityColor}55`,
                      border: `3px solid ${rarityColor}`,
                      maxWidth: 280,
                      width: '100%',
                    }}>
                      {/* Per-type image preview */}
                      {item?.item_type === 'task_effect' && item?.image_url && (
                        <Box sx={{
                          width: '100%', aspectRatio: '3/2', borderRadius: '12px', mx: 'auto', mb: 1.5,
                          border: `2px solid ${rarityColor}`,
                          overflow: 'hidden',
                        }}>
                          <Box component="img" src={item.image_url} alt={item.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      )}
                      {item?.item_type === 'task_animation' && (
                        <Box sx={{ mb: 1.5 }}>
                          <TaskButtonComponent
                            animationId={item.cosmetic_id || item.name?.toLowerCase().replace(/\s+/g, '-')}
                            label={item.name || 'Tehtävä'}
                            sublabel="Lahja"
                            icon="🎁"
                            coins={5}
                            bgColor={rarityColor}
                            staticMode={false}
                            playOnHover={false}
                            size="medium"
                          />
                        </Box>
                      )}
                      {item?.item_type === 'background' && item?.image_url && (
                        <Box sx={{
                          width: '100%', aspectRatio: '3/2', borderRadius: '12px', mx: 'auto', mb: 1.5,
                          border: `2px solid ${rarityColor}`,
                          overflow: 'hidden',
                        }}>
                          <Box component="img" src={item.image_url} alt={item.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      )}
                      {(item?.item_type === 'avatar') && item?.image_url && (
                        <Box sx={{
                          width: 120, height: 120, borderRadius: '50%', mx: 'auto', mb: 1.5,
                          border: `3px solid ${rarityColor}`,
                          overflow: 'hidden',
                          boxShadow: `0 4px 12px ${rarityColor}33`,
                        }}>
                          <Box component="img" src={item.image_url} alt={item.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      )}
                      {(item?.item_type === 'frame') && item?.image_url && (
                        <Box sx={{
                          width: 120, height: 120, mx: 'auto', mb: 1.5, position: 'relative',
                        }}>
                          <Box component="img" src={item.image_url} alt={item.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </Box>
                      )}
                      {item?.item_type !== 'task_effect' && item?.item_type !== 'task_animation' && item?.item_type !== 'background' && item?.item_type !== 'avatar' && item?.item_type !== 'frame' && item?.image_url && (
                        <Box sx={{
                          width: 100, height: 100, mx: 'auto', mb: 1.5,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: `2px solid ${rarityColor}`,
                        }}>
                          <Box component="img" src={item.image_url} alt={item.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </Box>
                      )}

                      {/* Item name */}
                      <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#333', mb: 1 }}>
                        {item?.name}
                      </Typography>

                      {/* Type + Rarity chips */}
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          label={`${TYPE_ICONS[item?.item_type] || '🛍️'} ${TYPE_LABELS[item?.item_type] || item?.item_type}`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 22,
                            bgcolor: 'rgba(0,0,0,0.06)',
                            color: '#555',
                          }}
                        />
                        <Chip
                          label={rarityLabel}
                          size="small"
                          sx={{
                            bgcolor: rarityColor,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 22,
                            boxShadow: item?.rarity === 'legendaarinen' ? `0 0 6px ${rarityColor}` : 'none',
                          }}
                        />
                      </Box>

                      {coinsAwarded > 0 && (
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5, color: '#333' }}>
                          Yhteensä: {oldBalance + coinsAwarded} 🪙
                        </Typography>
                      )}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tap to dismiss hint */}
      {phase === 'idle' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: 'absolute',
            bottom: '8%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 15,
          }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            Napauta jatkaaksesi
          </Typography>
        </motion.div>
      )}
    </Box>
  );
}
